/* ==========================================================================
   Superboba Bundle Funnel LP — JavaScript (V2 — Storefront API tokenless)
   Two-step bundle builder: pricing tiers → flavor selection → checkout

   Diff vs sb-lp-funnel.js: addToCart() builds the cart via a single
   Storefront API GraphQL `cartCreate` mutation and navigates to the returned
   `cart.checkoutUrl`. Replaces the legacy 4-RTT chain
   (cart/clear → cart/add+update → /discount/CODE prefetch → /discount/LAST?redirect=).

   Uses tokenless Storefront API access — Cart, Selling Plans, and discount
   codes are all in the tokenless feature set, so no access token is needed.
   See: https://shopify.dev/docs/storefronts/headless/building-with-the-storefront-api
   Query complexity well under the 1000 tokenless cap (~27 per cartCreate call).
   ========================================================================== */

(function () {
  'use strict';

  /* ========================================================================
     STATE
     ======================================================================== */
  const state = {
    currentStep: 1,
    selectedTier: 0,         // index of selected pricing tier (default: first/best deal)
    tierQuantity: 4,         // total packs needed for this tier
    flavors: {},             // { variantId: quantity }
    totalSelected: 0,        // sum of all flavor quantities
    carouselIndex: 0,
    carouselCount: 0,
    purchaseMode: 'sub',     // 'sub' (subscription tiers) | 'once' (try-once, no selling plan)
  };

  /* ========================================================================
     PURCHASE MODE (subscription vs try-once)
     'sub'  — classic flow: tier quantity, selling plan, tier discount codes.
     'once' — one-time purchase: minimum quantity from settings, NO selling
              plan, only the try-once discount code; gifts stay a subscription
              perk unless try_once_gifts is on.
     ======================================================================== */
  function tryOnceGiftsEnabled() {
    return state.purchaseMode !== 'once' ||
      (offerSection && offerSection.dataset.tryOnceGifts === 'true');
  }
  function setPurchaseMode(mode) {
    state.purchaseMode = mode;
    var once = mode === 'once';
    if (once) {
      state.tierQuantity = parseInt(offerSection.dataset.tryOnceQty) || 1;
    } else {
      var sel = offerSection.querySelector('.sb-funnel__tier--selected');
      if (sel) state.tierQuantity = parseInt(sel.dataset.quantity) || 1;
    }
    offerSection.classList.toggle('sb-funnel--once', once);
    offerSection.classList.toggle('sb-funnel--hide-gifts', !tryOnceGiftsEnabled());
  }

  /* ========================================================================
     SUBSCRIPTION SELLING PLAN MAPPING
     1-2 bags → 30-day plan, 3-4 bags → 60-day plan, 5+ bags → longer plan
     ======================================================================== */
  function getSellingPlanId(bagCount) {
    if (bagCount <= 2) return 4573528164;
    if (bagCount <= 4) return 4573593700;
    return 4582408292;
  }

  /* ========================================================================
     DISCOUNT CODE PER QUANTITY
     Match actual totalSelected bags to the tier block with the same quantity
     (1 → tier_1, 2 → tier_2, etc.). Falls back to highest tier for 5+ bags.
     ======================================================================== */
  function getDiscountCodeForQuantity(qty) {
    var allTiers = offerSection ? offerSection.querySelectorAll('.sb-funnel__tier') : null;
    if (!allTiers || allTiers.length === 0) return null;
    var exactMatch = null;
    var highestTier = null;
    var highestQty = 0;
    allTiers.forEach(function (t) {
      var tierQty = parseInt(t.dataset.quantity) || 0;
      if (tierQty === qty) exactMatch = t;
      if (tierQty > highestQty) {
        highestQty = tierQty;
        highestTier = t;
      }
    });
    var target = exactMatch || (qty > highestQty ? highestTier : null);
    return target ? target.dataset.discountCode : null;
  }

  /* ========================================================================
     DOM REFERENCES
     ======================================================================== */
  let offerSection, step1El, step2El, step2Nav;
  let carouselImages, carouselThumbs, carouselDots, prevBtn, nextBtn;
  let tierCards, giftBoxes, giftTitles;
  let step2GiftBoxes, step2GiftTitles;
  let flavorCards;
  let stickyBar, stickyBarCta, stickyBarText;
  let step2Cta, step2CtaText, step2Counter;
  let selectFlavorsCta;

  /* ========================================================================
     STEP SWITCHING
     ======================================================================== */
  function goToStep(stepNum, opts) {
    state.currentStep = stepNum;

    if (step1El) step1El.classList.toggle('hidden', stepNum !== 1);
    if (step2El) step2El.classList.toggle('hidden', stepNum !== 2);

    // Dispatch event for other sections to hide/show
    document.dispatchEvent(new CustomEvent('funnel:step-change', {
      detail: { step: stepNum }
    }));

    // Scroll to top — only on user-triggered step changes, never on init/reset
    if (offerSection && !(opts && opts.skipScroll)) {
      offerSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Lock body scroll on step 2, unlock on step 1
    document.body.classList.toggle('sb-funnel-step2-active', stepNum === 2);

    // Ensure position:fixed works on step2 nav by clearing containing block on all ancestors
    if (step2Nav && stepNum === 2) {
      var el = step2Nav.parentElement;
      while (el && el !== document.body) {
        el.style.transform = 'none';
        el.style.willChange = 'auto';
        el.style.filter = 'none';
        el.style.contain = 'none';
        el = el.parentElement;
      }
    }

    // Update step 2 counter
    if (stepNum === 2) {
      updateStep2Counter();
      updateStep2Gifts();
    }

    updateStickyBar();
  }

  /* ========================================================================
     IMAGE CAROUSEL (Step 1)
     ======================================================================== */
  function initCarousel() {
    const main = offerSection?.querySelector('.sb-funnel__carousel-main');
    if (!main) return;

    carouselImages = main.querySelectorAll('img');
    state.carouselCount = carouselImages.length;

    carouselThumbs = offerSection.querySelectorAll('.sb-funnel__carousel-thumb');
    carouselDots = offerSection.querySelectorAll('.sb-funnel__carousel-dot');
    prevBtn = offerSection.querySelector('.sb-funnel__carousel-arrow--prev');
    nextBtn = offerSection.querySelector('.sb-funnel__carousel-arrow--next');

    // Thumb clicks
    carouselThumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { setCarouselIndex(i); });
    });

    // Dot clicks
    carouselDots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { setCarouselIndex(i); });
    });

    // Arrow clicks
    if (prevBtn) prevBtn.addEventListener('click', function () { setCarouselIndex(state.carouselIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { setCarouselIndex(state.carouselIndex + 1); });

    // Touch swipe on mobile
    let startX = 0;
    main.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    main.addEventListener('touchend', function (e) {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) {
        setCarouselIndex(state.carouselIndex + (diff > 0 ? 1 : -1));
      }
    }, { passive: true });

    setCarouselIndex(0);
  }

  function setCarouselIndex(idx) {
    if (idx < 0) idx = 0;
    if (idx >= state.carouselCount) idx = state.carouselCount - 1;
    state.carouselIndex = idx;

    carouselImages.forEach(function (img, i) { img.classList.toggle('active', i === idx); });
    carouselThumbs.forEach(function (t, i) { t.classList.toggle('active', i === idx); });
    carouselDots.forEach(function (d, i) { d.classList.toggle('active', i === idx); });

    if (prevBtn) prevBtn.disabled = idx === 0;
    if (nextBtn) nextBtn.disabled = idx === state.carouselCount - 1;
  }

  /* ========================================================================
     PRICING TIER SELECTION (Step 1)
     ======================================================================== */
  function initTiers() {
    tierCards = offerSection?.querySelectorAll('.sb-funnel__tier:not(.sb-funnel__tier--soldout)');
    if (!tierCards) return;

    tierCards.forEach(function (card, i) {
      card.addEventListener('click', function () {
        // In theme editor, defer DOM mutations so Shopify's block selection runs first
        if (window.Shopify && window.Shopify.designMode) {
          setTimeout(function () { selectTier(card); }, 0);
        } else {
          selectTier(card);
        }
      });
    });

    // Select the configured default tier (from `data-default-tier-qty` on the
    // section root, set via theme editor). Falls back to the first non-soldout
    // tier if the configured qty doesn't match any tier or is sold out.
    var defaultQty = parseInt(offerSection.dataset.defaultTierQty) || 0;
    var defaultTier = null;
    if (defaultQty > 0) {
      tierCards.forEach(function (card) {
        if (!defaultTier && parseInt(card.dataset.quantity) === defaultQty) {
          defaultTier = card;
        }
      });
    }
    var fallbackTier = offerSection.querySelector('.sb-funnel__tier:not(.sb-funnel__tier--soldout)');
    var initialTier = defaultTier || fallbackTier;
    if (initialTier) selectTier(initialTier);
  }

  function selectTier(card) {
    var allTiers = offerSection.querySelectorAll('.sb-funnel__tier');
    allTiers.forEach(function (t) { t.classList.remove('sb-funnel__tier--selected'); });
    card.classList.add('sb-funnel__tier--selected');

    state.tierQuantity = parseInt(card.dataset.quantity) || 1;
    state.selectedTier = parseInt(card.dataset.tierIndex) || 0;

    // Update dynamic CTA details based on selected tier
    var frequency = card.dataset.frequency || '';
    var discount = card.dataset.discount || '';
    var salePrice = card.dataset.salePrice || '';
    var originalTotal = card.dataset.originalTotal || '';
    var qty = state.tierQuantity;

    // Update "Subscribe & Save" label
    var subLabel = offerSection.querySelector('.sb-funnel__sub-label');
    if (subLabel && frequency && discount) {
      subLabel.textContent = 'Subscribe & Save - ' + frequency + ' - ' + discount + ' First Order*';
    }

    // Update CTA button prices
    var priceOriginal = offerSection.querySelector('[data-step="1"] .sb-funnel__cta-price-original');
    var priceSale = offerSection.querySelector('[data-step="1"] .sb-funnel__cta-price-sale');
    if (priceOriginal && originalTotal) priceOriginal.textContent = originalTotal;
    if (priceSale && salePrice) priceSale.textContent = salePrice;

    // Update subscription benefit line — ship cadence based on tier bag count
    var benefitLine = offerSection.querySelector('.sb-funnel__sub-benefits-item');
    if (benefitLine) {
      var shipDays = qty <= 2 ? 30 : 60;
      benefitLine.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>' +
        'Your bundle ships in ' + shipDays + ' days';
    }

    updateStep1Gifts();
    updateStickyBar();
  }

  /* ========================================================================
     GIFT TIER PROGRESS
     ======================================================================== */
  function initGifts() {
    giftBoxes = offerSection?.querySelectorAll('[data-step="1"] .sb-funnel__gift');
    giftTitles = offerSection?.querySelectorAll('[data-step="1"] .sb-funnel__gift-title');
    step2GiftBoxes = offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift');
    step2GiftTitles = offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift-title');
  }

  function updateStep1Gifts() {
    if (!giftBoxes) return;
    giftBoxes.forEach(function (box, i) {
      var threshold = parseInt(box.dataset.unlockAt) || 1;
      var isUnlocked = state.tierQuantity >= threshold;
      box.classList.toggle('sb-funnel__gift--unlocked', isUnlocked);
    });
    if (giftTitles) {
      giftTitles.forEach(function (title, i) {
        var box = giftBoxes[i];
        if (!box) return;
        var threshold = parseInt(box.dataset.unlockAt) || 1;
        title.classList.toggle('sb-funnel__gift-title--active', state.tierQuantity >= threshold);
      });
    }
  }

  function updateStep2Gifts() {
    if (!step2GiftBoxes) return;
    step2GiftBoxes.forEach(function (box, i) {
      var threshold = parseInt(box.dataset.unlockAt) || 1;
      var isUnlocked = state.totalSelected >= threshold;
      box.classList.toggle('sb-funnel__gift--unlocked', isUnlocked);
    });
    if (step2GiftTitles) {
      step2GiftTitles.forEach(function (title, i) {
        var box = step2GiftBoxes[i];
        if (!box) return;
        var threshold = parseInt(box.dataset.unlockAt) || 1;
        title.classList.toggle('sb-funnel__gift-title--active', state.totalSelected >= threshold);
      });
    }
  }

  /* ========================================================================
     FLAVOR QUANTITY CONTROLS (Step 2)
     ======================================================================== */
  function initFlavors() {
    flavorCards = offerSection?.querySelectorAll('.sb-funnel__flavor');
    if (!flavorCards) return;

    flavorCards.forEach(function (card) {
      var variantId = card.dataset.variantId;
      var addBtn = card.querySelector('.sb-funnel__add-btn');
      var qtyBox = card.querySelector('.sb-funnel__qty-box');
      var qtyDisplay = card.querySelector('.sb-funnel__qty-display');
      var plusBtn = card.querySelector('.sb-funnel__qty-plus');
      var minusBtn = card.querySelector('.sb-funnel__qty-minus');

      if (addBtn) {
        addBtn.addEventListener('click', function () {
          state.flavors[variantId] = 1;
          addBtn.style.display = 'none';
          if (qtyBox) qtyBox.classList.add('visible');
          if (qtyDisplay) qtyDisplay.textContent = '1';
          card.classList.add('sb-funnel__flavor--active');
          recalcTotal();
        });
      }

      if (plusBtn) {
        plusBtn.addEventListener('click', function () {
          state.flavors[variantId] = (state.flavors[variantId] || 0) + 1;
          if (qtyDisplay) qtyDisplay.textContent = state.flavors[variantId];
          recalcTotal();
        });
      }

      if (minusBtn) {
        minusBtn.addEventListener('click', function () {
          state.flavors[variantId] = (state.flavors[variantId] || 0) - 1;
          if (state.flavors[variantId] <= 0) {
            delete state.flavors[variantId];
            // Reset to Add+ button
            if (addBtn) addBtn.style.display = '';
            if (qtyBox) qtyBox.classList.remove('visible');
            card.classList.remove('sb-funnel__flavor--active');
          } else {
            if (qtyDisplay) qtyDisplay.textContent = state.flavors[variantId];
          }
          recalcTotal();
        });
      }
    });
  }

  function recalcTotal() {
    state.totalSelected = 0;
    for (var key in state.flavors) {
      state.totalSelected += state.flavors[key];
    }
    updateStep2Gifts();
    updateStep2Counter();
    updateStep2AtcInfo();
    updateStickyBar();
  }

  /* ========================================================================
     STEP 2 CTA PRICES — "Go to Checkout  $150.00  $70.38"
     An exact tier match uses the prices step 1 promised. A count past the
     ladder scales the highest tier per bag. One-time mode uses the try-once
     price ladder from the section settings.
     ======================================================================== */
  function parseMoney(text) {
    var n = parseFloat(String(text || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? null : n;
  }
  function getTierElForQuantity(qty) {
    var allTiers = offerSection ? offerSection.querySelectorAll('.sb-funnel__tier') : null;
    if (!allTiers || !allTiers.length) return null;
    var exact = null, highest = null, highestQty = 0;
    allTiers.forEach(function (t) {
      var q = parseInt(t.dataset.quantity) || 0;
      if (q === qty) exact = t;
      if (q > highestQty) { highestQty = q; highest = t; }
    });
    return exact || (qty > highestQty ? highest : null);
  }

  /* Try-once price ladder from the section setting: "1:40, 2:76, 3:108".
     An exact bag count uses its own price. A count above the last entry uses
     the price per bag of that last entry. No ladder returns null. */
  var tryOnceLadder = null;
  function getTryOnceLadder() {
    if (tryOnceLadder) return tryOnceLadder;
    tryOnceLadder = [];
    var raw = (offerSection && offerSection.dataset.tryOncePrices) || '';
    raw.split(',').forEach(function (part) {
      var pair = part.split(':');
      var qty = parseInt(pair[0]);
      var price = parseFloat(pair[1]);
      if (qty > 0 && !isNaN(price)) tryOnceLadder.push({ qty: qty, price: price });
    });
    tryOnceLadder.sort(function (a, b) { return a.qty - b.qty; });
    return tryOnceLadder;
  }
  function getTryOncePrice(qty) {
    var ladder = getTryOnceLadder();
    if (!ladder.length || qty <= 0) return null;
    for (var i = 0; i < ladder.length; i++) {
      if (ladder[i].qty === qty) return ladder[i].price;
    }
    var last = ladder[ladder.length - 1];
    if (qty > last.qty) return (last.price / last.qty) * qty;
    return null;
  }

  function updateStep2CtaPrices() {
    var origEl = offerSection?.querySelector('[data-step2-original]');
    var saleEl = offerSection?.querySelector('[data-step2-sale]');
    if (!origEl || !saleEl) return;

    // The price pill has its own background. Hide the whole pill when there is
    // no price, or the button shows an empty brown pill.
    var pillEl = origEl.closest('.sb-funnel__cta-prices');
    function clear() {
      origEl.textContent = '';
      saleEl.textContent = '';
      if (pillEl) pillEl.hidden = true;
    }
    if (state.totalSelected === 0 || state.totalSelected < state.tierQuantity) { clear(); return; }

    // One-time mode uses the try-once price ladder from the section settings.
    // With no ladder, add up the product prices of the selected bags, but only
    // when no discount code applies at checkout.
    if (state.purchaseMode === 'once') {
      var onceTotal = getTryOncePrice(state.totalSelected);
      if (onceTotal == null) {
        if (offerSection.dataset.tryOnceDiscount) { clear(); return; }
        onceTotal = 0;
        for (var vid in state.flavors) {
          var card = offerSection.querySelector('[data-step="2"] .sb-funnel__flavor[data-variant-id="' + vid + '"]');
          var cents = card ? parseInt(card.dataset.price) : 0;
          if (!cents) { clear(); return; }
          onceTotal += (cents / 100) * state.flavors[vid];
        }
      }
      origEl.textContent = '';
      saleEl.textContent = '$' + onceTotal.toFixed(2);
      if (pillEl) pillEl.hidden = false;
      return;
    }

    var tier = getTierElForQuantity(state.totalSelected);
    if (!tier) { clear(); return; }
    var tierQty = parseInt(tier.dataset.quantity) || 0;
    var sale = parseMoney(tier.dataset.salePrice);
    var orig = parseMoney(tier.dataset.originalTotal);
    if (sale == null || orig == null || !tierQty) { clear(); return; }

    if (state.totalSelected !== tierQty) {
      sale = (sale / tierQty) * state.totalSelected;
      orig = (orig / tierQty) * state.totalSelected;
    }
    origEl.textContent = '$' + orig.toFixed(2);
    saleEl.textContent = '$' + sale.toFixed(2);
    if (pillEl) pillEl.hidden = false;
  }

  /* ========================================================================
     STEP 2 COUNTER + CTA
     ======================================================================== */
  function updateStep2Counter() {
    if (!step2Counter) return;
    step2Counter.textContent = 'Select at least ' + state.tierQuantity + ' bag' + (state.tierQuantity === 1 ? '' : 's');

    var remaining = state.tierQuantity - state.totalSelected;

    if (step2Cta) {
      if (remaining > 0) {
        step2Cta.disabled = true;
        step2Cta.classList.remove('sb-funnel__cta--ready');
        if (step2CtaText) step2CtaText.textContent = 'Select ' + remaining + ' more bag' + (remaining > 1 ? 's' : '');
      } else {
        step2Cta.disabled = false;
        step2Cta.classList.add('sb-funnel__cta--ready');
        step2Cta.classList.remove('sb-funnel__cta--loading');
        step2Cta.dataset.processing = 'false';
        if (step2CtaText) step2CtaText.textContent = 'Go to Checkout';
      }
    }
    updateStep2CtaPrices();
  }

  /* ========================================================================
     STEP 2 ATC INFO (savings + gifts unlocked)
     ======================================================================== */
  function updateStep2AtcInfo() {
    var savingEl = offerSection?.querySelector('[data-saving]') || document.querySelector('[data-saving]');
    var giftsEl = offerSection?.querySelector('[data-gifts]') || document.querySelector('[data-gifts]');

    // Calculate savings % from selected tier
    if (savingEl) {
      if (state.purchaseMode === 'once') {
        savingEl.textContent = 'One-time purchase — no autoship';
      } else {
        var selectedTier = offerSection?.querySelector('.sb-funnel__tier--selected');
        var discount = selectedTier?.dataset.discount || '0%';
        var pct = discount.replace(/[^0-9]/g, '') || '0';
        savingEl.textContent = state.totalSelected > 0 ? "You're saving " + pct + '%' : "You're saving 0%";
      }
    }

    // Count unlocked gifts
    if (giftsEl) {
      var allGifts = offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift') || [];
      var totalGifts = allGifts.length;
      var unlocked = 0;
      allGifts.forEach(function (box) {
        var threshold = parseInt(box.dataset.unlockAt) || 1;
        if (state.totalSelected >= threshold) unlocked++;
      });
      giftsEl.textContent = '✨ GIFTS UNLOCKED: ' + unlocked + '/' + totalGifts;
    }
  }

  /* ========================================================================
     STICKY BOTTOM BAR
     ======================================================================== */
  function updateStickyBar() {
    if (!stickyBar) return;

    if (state.currentStep === 1) {
      // Visibility is controlled by scroll observer, just update text/state
      if (stickyBarText) stickyBarText.textContent = 'Select Flavors';
      if (stickyBarCta) {
        stickyBarCta.disabled = false;
        stickyBarCta.classList.remove('sb-funnel__cta--ready');
      }
    } else {
      // Hide sticky bar in step 2 (step 2 has its own sticky ATC)
      stickyBar.style.display = 'none';
    }
  }

  function initStickyBarObserver() {
    if (!stickyBar || !selectFlavorsCta) return;

    // Hide sticky bar by default
    stickyBar.style.display = 'none';

    // Show sticky bar only when the main "Select Flavors" CTA scrolls out of view
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (state.currentStep !== 1) return;
        if (entry.isIntersecting) {
          // Main CTA is visible — hide sticky bar
          stickyBar.style.display = 'none';
        } else {
          // Main CTA scrolled out of view — show sticky bar
          stickyBar.style.display = '';
        }
      });
    }, { threshold: 0 });

    observer.observe(selectFlavorsCta);
  }

  /* ========================================================================
     DEBUG STATUS PANEL (visible on mobile when ?debug=1 is in URL)
     ======================================================================== */
  function isDebug() {
    try { return new URLSearchParams(window.location.search).get('debug') === '1'; }
    catch (e) { return false; }
  }
  function debugStatus(msg, isError) {
    if (!isDebug()) return;
    var el = document.getElementById('sb-funnel-debug');
    if (!el) {
      el = document.createElement('div');
      el.id = 'sb-funnel-debug';
      el.style.cssText = 'position:fixed;bottom:0;left:0;right:0;background:#111;color:#fff;font:12px/1.3 monospace;padding:8px 12px;z-index:999999;max-height:40vh;overflow:auto;white-space:pre-wrap;word-break:break-all';
      document.body.appendChild(el);
    }
    var line = document.createElement('div');
    line.style.color = isError ? '#ff6b6b' : '#b2f5b2';
    line.textContent = new Date().toLocaleTimeString() + ' ' + msg;
    el.appendChild(line);
    el.scrollTop = el.scrollHeight;
  }

  /* ========================================================================
     CART INTEGRATION
     ======================================================================== */
  function addToCart() {
    // Build cart lines (flavors). Variant + selling plan are GIDs in Storefront API.
    // One-time mode buys the plain variant, so it carries no selling plan.
    var sellingPlanIdNum = state.purchaseMode === 'once' ? null : getSellingPlanId(state.totalSelected);
    var flavorLines = [];
    for (var variantId in state.flavors) {
      if (state.flavors[variantId] > 0) {
        var line = {
          merchandiseId: 'gid://shopify/ProductVariant/' + parseInt(variantId),
          quantity: state.flavors[variantId]
        };
        if (sellingPlanIdNum) line.sellingPlanId = 'gid://shopify/SellingPlan/' + sellingPlanIdNum;
        flavorLines.push(line);
      }
    }

    if (flavorLines.length === 0) {
      debugStatus('addToCart: 0 flavor items, aborting', true);
      return;
    }

    // Gift lines — best-effort. If a sold-out gift makes cartCreate return
    // userErrors, we retry once with flavors-only so the order still proceeds.
    var giftLines = [];
    var giftBoxesEls = offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift') || [];
    giftBoxesEls.forEach(function (box) {
      if (!tryOnceGiftsEnabled()) return;
      var threshold = parseInt(box.dataset.unlockAt) || 1;
      var giftVariant = box.dataset.giftVariant;
      if (giftVariant && state.totalSelected >= threshold) {
        giftLines.push({
          merchandiseId: 'gid://shopify/ProductVariant/' + parseInt(giftVariant),
          quantity: 1,
          attributes: [{ key: '_gift', value: 'true' }]
        });
      }
    });

    // Discount codes: tier code (auto-bumps with quantity), per-gift unlocks,
    // global gift code. Dedupe. Storefront API takes them as a single array
    // and applies all combinable codes in one mutation.
    var discountCodes = [];
    var seen = {};
    function pushCode(c) {
      if (!c) return;
      var t = c.trim();
      if (!t || seen[t]) return;
      seen[t] = true;
      discountCodes.push(t);
    }
    if (state.purchaseMode === 'once') {
      pushCode(offerSection?.dataset.tryOnceDiscount);
    } else {
      pushCode(getDiscountCodeForQuantity(state.totalSelected));
    }
    if (tryOnceGiftsEnabled()) {
      giftBoxesEls.forEach(function (box) {
        var threshold = parseInt(box.dataset.unlockAt) || 1;
        if (state.totalSelected >= threshold) pushCode(box.dataset.giftDiscountCode);
      });
      pushCode(offerSection?.dataset.giftDiscount);
    }

    // Cart attributes — Shopify Flow rule on `multi-step-lp` tags the order
    // with `lander:<slug>` and `tier:<qty>`. Storefront API persists these
    // through cart → checkout → order, same as REST AJAX cart attributes.
    var funnelSlug = (offerSection && offerSection.dataset.funnelSlug) ? offerSection.dataset.funnelSlug : 'mt-ms-01';
    var offerSource = (offerSection && offerSection.dataset.offerSource) ? offerSection.dataset.offerSource : 'paid-offer-bundle';
    var attributes = [
      { key: 'multi-step-lp', value: funnelSlug },
      { key: 'tier', value: String(state.totalSelected) },
      { key: 'source', value: offerSource },
      { key: 'purchase_type', value: state.purchaseMode === 'once' ? 'one-time' : 'subscription' }
    ];

    debugStatus('payload: ' + flavorLines.length + ' flavors + ' + giftLines.length + ' gifts, ' + discountCodes.length + ' codes');

    // Disable button during request. Drop --ready so its ::before/::after
    // (shimmer/glow) don't fight the --loading spinner ::after.
    if (step2Cta) {
      step2Cta.disabled = true;
      step2Cta.classList.remove('sb-funnel__cta--ready');
      step2Cta.classList.add('sb-funnel__cta--loading');
    }
    function setStageText(txt) {
      if (step2CtaText) step2CtaText.textContent = txt;
    }
    setStageText('Building your order...');

    // Safety timeout: if nothing navigates in 15s (slow mobile networks,
    // failed redirects), re-enable the button so the user isn't stuck.
    var recoveryTimeout = setTimeout(function () {
      if (step2Cta) {
        step2Cta.disabled = false;
        step2Cta.classList.remove('sb-funnel__cta--loading');
        step2Cta.dataset.processing = 'false';
      }
    }, 15000);
    function clearRecovery() { clearTimeout(recoveryTimeout); }
    function resetCtaState() {
      clearRecovery();
      if (step2Cta) {
        step2Cta.disabled = false;
        step2Cta.classList.remove('sb-funnel__cta--loading');
        step2Cta.dataset.processing = 'false';
      }
    }

    // Same-origin Storefront API endpoint. Tokenless — Cart + Selling Plans
    // + discount codes are all in the tokenless feature set.
    var endpoint = '/api/2024-10/graphql.json';
    var mutation = 'mutation cartCreate($input: CartInput!) {' +
      ' cartCreate(input: $input) {' +
      '   cart { checkoutUrl }' +
      '   userErrors { field message code }' +
      ' }' +
      '}';

    function callCartCreate(lines) {
      return fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          query: mutation,
          variables: {
            input: {
              lines: lines,
              discountCodes: discountCodes,
              attributes: attributes
            }
          }
        })
      }).then(function (res) {
        if (res.ok) return res.json();
        return res.text().then(function (body) {
          var err = new Error('HTTP ' + res.status + ': ' + body.slice(0, 200));
          err.status = res.status;
          throw err;
        });
      });
    }

    debugStatus('cartCreate: ' + funnelSlug);
    console.log('[sb-funnel-v2] cartCreate slug=' + funnelSlug + ' codes=', discountCodes);

    callCartCreate(flavorLines.concat(giftLines))
      .then(function (json) {
        var checkoutUrl = json && json.data && json.data.cartCreate && json.data.cartCreate.cart && json.data.cartCreate.cart.checkoutUrl;
        var userErrors = (json && json.data && json.data.cartCreate && json.data.cartCreate.userErrors) || [];
        var topErrors = (json && json.errors) || [];

        if (checkoutUrl) {
          clearRecovery();
          debugStatus('navigate → ' + checkoutUrl);
          console.log('[sb-funnel-v2] navigate →', checkoutUrl);
          window.location.href = checkoutUrl;
          return;
        }

        // No checkoutUrl. Most common cause: a gift variant is sold out and
        // cartCreate rejected the whole call. Retry once without gifts.
        if (giftLines.length > 0) {
          debugStatus('cartCreate userErrors → retry without gifts: ' + JSON.stringify(userErrors).slice(0, 120), true);
          return callCartCreate(flavorLines).then(function (j2) {
            var url2 = j2 && j2.data && j2.data.cartCreate && j2.data.cartCreate.cart && j2.data.cartCreate.cart.checkoutUrl;
            if (url2) {
              clearRecovery();
              console.log('[sb-funnel-v2] retry navigate →', url2);
              window.location.href = url2;
              return;
            }
            throw new Error('cartCreate retry failed: ' + JSON.stringify((j2 && j2.data && j2.data.cartCreate && j2.data.cartCreate.userErrors) || (j2 && j2.errors) || j2));
          });
        }

        throw new Error('cartCreate failed: ' + JSON.stringify(userErrors.length ? userErrors : topErrors));
      })
      .catch(function (err) {
        console.error('cartCreate failed:', err);
        debugStatus('EXCEPTION: ' + (err && err.message ? err.message : String(err)), true);
        resetCtaState();
      });
  }

  /* ========================================================================
     CONTENT SECTION VISIBILITY
     ======================================================================== */
  document.addEventListener('funnel:step-change', function (e) {
    // Hide all sections between the offer and sticky bar when on step 2
    // Target by section class names AND data attribute
    var selectors = [
      '.sb-lp-funnel-benefits-section',
      '.sb-lp-funnel-social-section',
      '.sb-lp-funnel-sticky-bar-section',
      '[data-funnel-content]'
    ];

    // Also find any shopify-section that wraps our reused sections
    var allSections = document.querySelectorAll('.shopify-section');
    allSections.forEach(function (section) {
      // Skip the offer section itself
      if (section.querySelector('#sb-funnel-offer')) return;
      // Skip if it's the sticky bar
      if (section.querySelector('.sb-funnel-sticky')) {
        if (e.detail.step === 2) {
          section.classList.add('sb-funnel-hidden');
        } else {
          section.classList.remove('sb-funnel-hidden');
        }
        return;
      }
      // Check if this section is between offer and sticky
      var offerEl = document.getElementById('sb-funnel-offer');
      if (!offerEl) return;
      var offerSectionEl = offerEl.closest('.shopify-section');
      if (!offerSectionEl) return;

      // Compare DOM order
      if (offerSectionEl.compareDocumentPosition(section) & Node.DOCUMENT_POSITION_FOLLOWING) {
        if (e.detail.step === 2) {
          section.classList.add('sb-funnel-hidden');
        } else {
          section.classList.remove('sb-funnel-hidden');
        }
      }
    });
  });

  /* ========================================================================
     COUNTDOWN TIMER
     ======================================================================== */
  function initCountdown() {
    var timerEl = document.querySelector('[data-funnel-countdown]');
    if (!timerEl) return;

    var endStr = timerEl.dataset.funnelCountdown;
    var endDate;

    if (endStr) {
      endDate = new Date(endStr);
    } else {
      // Rolling 3-day countdown
      endDate = new Date();
      endDate.setDate(endDate.getDate() + 3);
      endDate.setHours(23, 59, 59, 0);
    }

    var hrsEl = timerEl.querySelector('[data-hours]');
    var minEl = timerEl.querySelector('[data-minutes]');
    var secEl = timerEl.querySelector('[data-seconds]');

    function tick() {
      var now = Date.now();
      var diff = endDate.getTime() - now;
      if (diff <= 0) diff = 0;

      var hrs = Math.floor(diff / 3600000);
      var mins = Math.floor((diff % 3600000) / 60000);
      var secs = Math.floor((diff % 60000) / 1000);

      if (hrsEl) hrsEl.textContent = String(hrs).padStart(2, '0');
      if (minEl) minEl.textContent = String(mins).padStart(2, '0');
      if (secEl) secEl.textContent = String(secs).padStart(2, '0');
    }

    tick();
    setInterval(tick, 1000);
  }

  /* ========================================================================
     INIT
     ======================================================================== */
  function init() {
    offerSection = document.getElementById('sb-funnel-offer');
    if (!offerSection) return;

    step1El = offerSection.querySelector('[data-step="1"]');
    step2El = offerSection.querySelector('[data-step="2"]');

    // Step 1 elements
    selectFlavorsCta = offerSection.querySelector('[data-action="select-flavors"]');
    if (selectFlavorsCta) {
      selectFlavorsCta.addEventListener('click', function () {
        setPurchaseMode('sub');
        goToStep(2);
      });
    }

    var tryOnceCta = offerSection.querySelector('[data-action="try-once"]');
    if (tryOnceCta) {
      tryOnceCta.addEventListener('click', function () {
        setPurchaseMode('once');
        goToStep(2);
      });
    }

    // Step 2 elements
    step2Nav = offerSection.querySelector('.sb-funnel__step2-nav');
    var backBtn = offerSection.querySelector('.sb-funnel__back-btn');
    if (backBtn) {
      backBtn.addEventListener('click', function () { goToStep(1); });
    }

    step2Cta = offerSection.querySelector('[data-action="checkout"]');
    step2CtaText = step2Cta?.querySelector('.sb-funnel__cta-text');
    step2Counter = offerSection.querySelector('.sb-funnel__step2-title');

    if (step2Cta) {
      step2Cta.addEventListener('click', function () {
        if (step2Cta.disabled) return;
        // Prevent double-submit from rapid taps on mobile
        if (step2Cta.dataset.processing === 'true') return;
        step2Cta.dataset.processing = 'true';
        addToCart();
      });
    }

    // Sticky bar
    stickyBar = document.querySelector('.sb-funnel-sticky');
    stickyBarCta = stickyBar?.querySelector('.sb-funnel__cta');
    stickyBarText = stickyBar?.querySelector('.sb-funnel__cta-text');

    if (stickyBarCta) {
      stickyBarCta.addEventListener('click', function () {
        if (state.currentStep !== 1) return;
        // Scroll back to the quantity selector so the user can review tiers,
        // then hit the main CTA to advance.
        var tiersEl = offerSection?.querySelector('.sb-funnel__tiers');
        (tiersEl || offerSection)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    // Initialize components
    initCarousel();
    initGifts();
    initTiers();
    initFlavors();
    initCountdown();
    initStickyBarObserver();
    updateStickyBar();
  }

  /* ========================================================================
     RESET ON RETURN — if the user hits back from checkout, reset to step 1
     with no flavors selected so they can't accidentally reuse stale state
     ======================================================================== */
  function resetFunnelState() {
    state.flavors = {};
    state.totalSelected = 0;
    if (flavorCards) {
      flavorCards.forEach(function (card) {
        card.classList.remove('sb-funnel__flavor--active');
        var addBtn = card.querySelector('.sb-funnel__add-btn');
        var qtyBox = card.querySelector('.sb-funnel__qty-box');
        var qtyDisplay = card.querySelector('.sb-funnel__qty-display');
        if (addBtn) addBtn.style.display = '';
        if (qtyBox) qtyBox.classList.remove('visible');
        if (qtyDisplay) qtyDisplay.textContent = '1';
      });
    }
    if (step2Cta) {
      step2Cta.disabled = false;
      step2Cta.classList.remove('sb-funnel__cta--loading');
      step2Cta.dataset.processing = 'false';
    }
    goToStep(1, { skipScroll: true });
  }

  // Fires on fresh loads AND on bfcache restore (browser back from checkout)
  window.addEventListener('pageshow', function (event) {
    if (!offerSection) return;
    resetFunnelState();
  });

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
